import { If } from "@/types/logic"
import { IsOpenString } from "@/types/typechecks"

type TypeGuard<GuardedType> = (value: unknown) => value is GuardedType

const not =
  <ExcludedType>(typeGuard: TypeGuard<ExcludedType>) =>
  <T>(value: ExcludedType | T): value is T =>
    !typeGuard(value)

const isNull = (value: unknown): value is null => value === null
const isUndefined = (value: unknown): value is undefined =>
  typeof value === "undefined"
const isNullish = (value: unknown): value is null | undefined =>
  isNull(value) || isUndefined(value)

const isNonNull = not(isNull)
const isDefined = not(isUndefined)
const hasValue = not(isNullish)

const isString = (value: unknown): value is string => typeof value === "string"
const isNumber = (value: unknown): value is number =>
  typeof value === "number" && !isNaN(value)

const isArray = (value: unknown): value is unknown[] => Array.isArray(value)

const isObject = (value: unknown): value is Record<string, unknown> =>
  !isNull(value) && typeof value === "object"
const hasProperty =
  <Key extends string, PropertyType = unknown>(
    key: Key,
    typeGuard?: TypeGuard<PropertyType>,
  ) =>
  (
    value: unknown,
  ): value is If<IsOpenString<Key>, unknown, { [J in Key]: PropertyType }> => {
    return (
      !isNullish(value) &&
      isObject(value) &&
      Object.prototype.hasOwnProperty.call(value, key) &&
      (isUndefined(typeGuard) || typeGuard(value[key]))
    )
  }

export {
  hasProperty,
  hasValue,
  isArray,
  isDefined,
  isNonNull,
  isNull,
  isNullish,
  isNumber,
  isObject,
  isString,
  isUndefined,
}
